/**
 * The two calculations behind the models screen's usage columns and its
 * deprecation panel, kept out of the component so they can be reasoned about
 * — and run — on their own.
 *
 * No imports, deliberately. Node 24 strips types, so a module with no imports
 * and no JSX runs directly under `node model-usage.ts`, which is the only
 * unit-test mechanism the TypeScript side of this workspace has (there is no
 * test runner anywhere in it). The row shapes below are declared structurally
 * rather than imported from admin-client for exactly that reason; they are
 * subsets of `ModelUsage` and `ModelPolicy`, so the real documents are
 * assignable to them and the compiler still checks the join.
 *
 * Both calculations are mirrors of something the server does, and both would
 * fail silently if they drifted:
 *
 *   * totalsByModel re-keys the usage report. The API answers per (alias,
 *     provider, provider_model) triple and the catalogue table has one row per
 *     (provider, model), so the screen has to sum over aliases. Getting the
 *     key wrong shows one alias's traffic as the whole model's.
 *   * reachableAliases is checkReplacementReachable in internal/admin/
 *     catalogue.go, re-run in the browser so the picker only offers aliases the
 *     write will accept. The server is still the control — it re-checks inside
 *     the transaction that does the write, and answers 409 — this only spares
 *     the operator from being refused after they have already typed a reason.
 */

/** One row of the usage report, cut down to what this module reads. */
export interface UsageRow {
  alias: string;
  provider: string;
  provider_model: string;
  requests: number;
  /** The sum of per-day distinct counts — an upper bound on people, not a
   *  distinct count. See ModelUsage in admin-client for why it is named that. */
  distinct_users_daily_sum: number;
}

/** One catalogue row's traffic, summed over every alias that reached it. */
export interface UsageTotals {
  requests: number;
  /** Still an upper bound, and looser than the per-alias one it came from: a
   *  person who used this model through two aliases is in it twice more. It
   *  answers "at most how many people would a deprecation notice reach", which
   *  is the question the panel asks, and it is not "how many people use this". */
  usersUpperBound: number;
  /** The aliases callers actually reached this model by, most traffic first.
   *  These are the names that have to be told to move somewhere else. */
  aliases: string[];
}

/**
 * The map key for a catalogue row.
 *
 * A NUL joiner rather than a colon or a slash: a provider name is constrained
 * but a provider's own model id is whatever the provider spells it, and two
 * distinct pairs that join into one string would silently merge two models'
 * traffic. Nothing that reaches this map can contain a NUL — it arrives as
 * JSON from Postgres text columns, which cannot hold one.
 */
export function usageKey(provider: string, model: string): string {
  return `${provider}\u0000${model}`;
}

/**
 * Re-key the usage report from (alias, provider, provider_model) onto the
 * catalogue's own (provider, model).
 *
 * A model that is not in the returned map has NO row in the report, which is a
 * different fact from a zero and must be rendered differently — and, when the
 * report says it was truncated, is a different fact again from "no traffic".
 * The map deliberately cannot express either of those: it holds only what was
 * measured, and the screen decides what an absence means from the report's own
 * `truncated` flag.
 */
export function totalsByModel(rows: UsageRow[]): Map<string, UsageTotals> {
  const totals = new Map<string, UsageTotals>();
  // Aliases are collected with their request counts so the list can be ordered
  // by traffic; the API's own ORDER BY is over the whole report, not within one
  // model, so it cannot be leaned on once the rows are regrouped.
  const aliasTraffic = new Map<string, Map<string, number>>();
  for (const row of rows) {
    const key = usageKey(row.provider, row.provider_model);
    const current = totals.get(key);
    if (current === undefined) {
      totals.set(key, {
        requests: row.requests,
        usersUpperBound: row.distinct_users_daily_sum,
        aliases: [],
      });
    } else {
      current.requests += row.requests;
      current.usersUpperBound += row.distinct_users_daily_sum;
    }
    const aliases = aliasTraffic.get(key) ?? new Map<string, number>();
    aliases.set(row.alias, (aliases.get(row.alias) ?? 0) + row.requests);
    aliasTraffic.set(key, aliases);
  }
  for (const [key, aliases] of aliasTraffic) {
    const total = totals.get(key);
    if (total === undefined) {
      continue;
    }
    total.aliases = [...aliases.entries()]
      .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
      .map(([alias]) => alias);
  }
  return totals;
}

export type UsageColumn = "users" | "requests";
export type SortDirection = "desc" | "asc";

function measure(totals: UsageTotals | undefined, column: UsageColumn): number | undefined {
  if (totals === undefined) {
    return undefined;
  }
  return column === "users" ? totals.usersUpperBound : totals.requests;
}

/**
 * Order two catalogue rows by one usage column.
 *
 * A model with no measurement sorts LAST in both directions, which is the one
 * decision in here worth arguing about. Ascending by requests is "show me the
 * quietest models" and an unmeasured row is not the quietest — it is a row the
 * report says nothing about, and floating it to the top of the list an operator
 * is scanning for deprecation candidates would present "we did not measure
 * this" as "nobody uses this". That is the same confusion the dash exists to
 * prevent, so it is not undone by the sort.
 */
export function compareUsage(
  a: UsageTotals | undefined,
  b: UsageTotals | undefined,
  column: UsageColumn,
  direction: SortDirection,
): number {
  const left = measure(a, column);
  const right = measure(b, column);
  if (left === undefined || right === undefined) {
    if (left === right) {
      return 0;
    }
    return left === undefined ? 1 : -1;
  }
  return direction === "desc" ? right - left : left - right;
}

/** One model policy, cut down to what the reachability check reads. */
export interface PolicyRow {
  profileID: string;
  /** The client-facing ALIAS the policy admits. */
  model: string;
  provider: string;
  providerModel: string;
  allowed: boolean;
}

export interface Reachable {
  /** False when no routing profile permits the model at all — the server's
   *  ErrModelUnrouted, and the reason the picker has nothing to offer rather
   *  than the reason it has nothing to offer YET. */
  routed: boolean;
  /** Aliases a replacement may name, alphabetically. Every one of them is
   *  permitted by a profile that also permits the model being deprecated, and
   *  is served there by some OTHER catalogue row. */
  aliases: string[];
}

/**
 * The aliases a deprecation of (provider, model) may point callers at.
 *
 * This is checkReplacementReachable's three-part question, in the same order
 * and with the same `allowed` predicate on every side of it:
 *
 *   1. which profiles permit this model — a policy row with allowed = false is
 *      a denial somebody wrote on purpose and does not count as permission;
 *   2. which aliases those profiles permit — anything else would send callers
 *      to a name their own profile refuses;
 *   3. of those, which are served by a catalogue row OTHER than the one being
 *      deprecated — an alias served only by this model is advice that loops
 *      straight back to it.
 *
 * Only step 3's survivors are offered. The two it removes are the two 409s the
 * write would come back with, so the picker cannot produce either.
 */
export function reachableAliases(
  policies: PolicyRow[],
  provider: string,
  model: string,
): Reachable {
  const profiles = new Set<string>();
  for (const policy of policies) {
    if (policy.allowed && policy.provider === provider && policy.providerModel === model) {
      profiles.add(policy.profileID);
    }
  }
  if (profiles.size === 0) {
    return { routed: false, aliases: [] };
  }
  const elsewhere = new Set<string>();
  for (const policy of policies) {
    if (!policy.allowed || !profiles.has(policy.profileID)) {
      continue;
    }
    if (policy.provider === provider && policy.providerModel === model) {
      continue;
    }
    elsewhere.add(policy.model);
  }
  return { routed: true, aliases: [...elsewhere].sort() };
}
